import { FC, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import * as z from "zod";

import { Button } from "@/components/_atoms/button";
import { Card, CardContent, CardHeader } from "@/components/_atoms/card";
import { Form } from "@/components/_atoms/form";
import { useUnsafeCurrentUserContext } from "@/domain/user/hooks/use-current-user-context";
import { AvatarWorkspace } from "@/domain/workspace/components/avatar-workspace";
import { acceptWorkspaceInviteMutationDocument } from "@/domain/workspace/hooks/api/workspace-membership.gql";
import { WORKSPACE_MEMBERSHIP_STATUS } from "@/domain/workspace/lib/workspace-membership-status";
import { Workspace } from "@/domain/workspace/models/workspace-model";
import { AcceptWorkspaceInviteMutation, WorkspaceInviteAcceptInput } from "@/graphql/generated/graphql";
import { cn } from "@/lib/cn";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";

import { FederatedLoginProp, UserProp } from "../routes/accept-invite-workspace.page";
import { requestPasswordResetLink, signout } from "../services/auth.service";

import { AcceptInviteButton } from "./accept-invite-button";
import { FederatedContinueButton } from "./federated-continue-button";
import { InviteTextField } from "./invite-text-field";
import { resolveInviteCardView } from "./resolve-invite-card-view";

export interface AcceptWorkspaceInviteCardProps {
  token: string;
  workspace: Workspace;
  user: UserProp;
  federatedLogin: FederatedLoginProp | null;
}

interface ContinueAction {
  label: string;
  execute: () => void;
}

export const AcceptWorkspaceInviteCard: FC<AcceptWorkspaceInviteCardProps> = ({
  token,
  workspace,
  user,
  federatedLogin,
}) => {
  const { t } = useTranslation("auth");
  const [continueAction, setContinueAction] = useState<ContinueAction | null>(null);
  const [isAwaitingFederatedLogin, setIsAwaitingFederatedLogin] = useState(false);

  const formSchema = useMemo(
    () =>
      z
        .object({
          email: z.string().email().optional(),
          password: z.string().min(8, {
            message: t("invite.workspace.passwordMinLength"),
          }),
          confirmation: z.string(),
          fullname: z
            .string()
            .min(3, {
              message: t("invite.workspace.nameMinLength"),
            })
            .optional(),
          username: z
            .string()
            .min(3, {
              message: t("invite.workspace.usernameMinLength"),
            })
            .optional(),
        })
        .refine((data) => data.password === data.confirmation, {
          message: t("invite.workspace.passwordMismatch"),
          path: ["confirmation"],
        }),
    [t],
  );

  const passwordOnlySchema = useMemo(
    () =>
      z.object({
        password: z.string().min(1),
      }),
    [],
  );

  const { mutate: acceptInvitation, isPending: loading } = useMutation({
    mutationFn: async (input: WorkspaceInviteAcceptInput) => {
      const client = createOperationClient("acceptWorkspaceInvite");
      return await client.request(acceptWorkspaceInviteMutationDocument, { input });
    },
    onSuccess: onUpdateCompleted,
    onError: onUpdateError,
  });

  const context = useUnsafeCurrentUserContext();
  const currentUser = context?.currentUser;

  const federatedStartUrl = federatedLogin?.startUrl ?? null;
  const view = resolveInviteCardView({
    hasContinueAction: continueAction !== null,
    isAwaitingFederatedLogin,
    isFederated: federatedLogin !== null,
    federatedStartUrl,
  });
  const canShowPasswordForm = view === "passwordForm";

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: user.email,
      password: "",
      confirmation: "",
      fullname: "",
      username: "",
    },
  });

  const passwordOnlyForm = useForm<z.infer<typeof passwordOnlySchema>>({
    resolver: zodResolver(passwordOnlySchema),
    defaultValues: {
      password: "",
    },
  });

  function isInvitationForAuthenticatedAccount(): boolean {
    return currentUser?.email === user.email;
  }

  function handleAcceptInvitePressed() {
    acceptInvitation({
      token,
      email: user.email,
    });
  }

  function onSubmitActivatedUserWithPassword(values: z.infer<typeof passwordOnlySchema>) {
    acceptInvitation({
      token,
      email: user.email,
      password: values.password,
    });
  }

  function onSubmitNewUserWithPassword(values: z.infer<typeof formSchema>) {
    acceptInvitation({
      token,
      email: user.email,
      password: values.password,
      username: values.username,
      fullname: values.fullname,
    });
  }

  const { mutate: sendPasswordReset, isPending: sendingReset } = useMutation({
    mutationFn: async () => requestPasswordResetLink(user.email),
    onSuccess: () => {
      toast.success(t("invite.workspace.resetLinkSentTitle"), {
        description: t("invite.workspace.resetLinkSentDescription", { email: user.email }),
      });
    },
    onError: () => {
      toast.error(t("invite.inviteError"), { description: t("invite.inviteErrorDescription") });
    },
  });

  function onUpdateCompleted({ acceptWorkspaceInvite }: AcceptWorkspaceInviteMutation) {
    if (acceptWorkspaceInvite.status !== WORKSPACE_MEMBERSHIP_STATUS.ACTIVE) {
      setIsAwaitingFederatedLogin(true);
      return;
    }

    if (isInvitationForAuthenticatedAccount()) {
      setContinueAction({
        label: t("invite.continueToWorkspace"),
        execute: () => window.location.replace(new URL(`${window.location.origin}/${workspace.workspaceUrl}`)),
      });
    } else if (currentUser && currentUser.email !== user.email) {
      setContinueAction({
        label: t("invite.continueToLogin"),
        execute: () => signout(),
      });
    } else {
      setContinueAction({
        label: t("invite.continueToLogin"),
        execute: () => window.location.replace(new URL(`${window.location.origin}`)),
      });
    }

    toast.success(t("invite.workspace.welcomeTitle", { name: workspace.name }), { description: t("invite.accepted") });
  }

  function onUpdateError() {
    toast.error(t("invite.inviteError"), { description: t("invite.inviteErrorDescription") });
  }

  return (
    <Card className="z-10 max-h-screen w-full max-w-xl overflow-hidden overflow-y-scroll border p-6 shadow-xl">
      <CardHeader>
        <div className="flex flex-col gap-4">
          <AvatarWorkspace workspace={workspace} className="h-16 w-16 text-2xl" />
          <h2 className="text-xl">{t("invite.workspace.title", { name: workspace.name })}</h2>
          <p className="text-md font-light text-muted-foreground">{t("invite.arkDescription")}</p>
          <div className="w-full border-t border-solid" />
        </div>
      </CardHeader>
      <CardContent>
        {view === "accepted" && continueAction && (
          <div className="mb-10 mt-4 flex flex-col gap-4">
            <p className="mb-2 text-base font-light text-foreground" role="status">
              {t("invite.accepted")}
            </p>
            <Button
              autoFocus
              onClick={() => continueAction.execute()}
              variant="default"
              className="h-10 w-48 items-center justify-center text-sm"
            >
              {continueAction.label}
            </Button>
          </div>
        )}

        {view === "federatedStart" && federatedStartUrl !== null && (
          <div className="mb-10 mt-4 flex flex-col gap-4">
            <p className="mb-2 text-base font-light text-foreground">
              {t("invite.workspace.federatedDescription", { email: user.email })}
            </p>
            <FederatedContinueButton startUrl={federatedStartUrl} />
          </div>
        )}

        {view === "federatedPending" && (
          <div className="mb-10 mt-4 flex flex-col gap-4">
            <p className="mb-2 text-base font-light text-foreground" role="status">
              {t("invite.workspace.federatedPending", { workspace: workspace.name })}
            </p>
            {federatedStartUrl !== null && <FederatedContinueButton startUrl={federatedStartUrl} />}
          </div>
        )}

        {canShowPasswordForm && user.hasSetPassword && isInvitationForAuthenticatedAccount() && (
          <div className="mb-10 mt-4 flex flex-col gap-4">
            <p className="mb-2 text-base font-light text-foreground">
              {t("invite.workspace.acceptActivated", { email: user.email })}.
            </p>
            <AcceptInviteButton isLoading={loading} onClick={handleAcceptInvitePressed} />
          </div>
        )}

        {canShowPasswordForm && user.hasSetPassword && !isInvitationForAuthenticatedAccount() && (
          <div className="mb-10 mt-4 flex flex-col gap-4">
            <p className="mb-2 text-base font-light text-foreground">
              {t("invite.workspace.acceptActivated", { email: user.email })}
              {t("invite.workspace.acceptActivatedSuffix")}
            </p>
            <Form {...passwordOnlyForm}>
              <form
                onSubmit={passwordOnlyForm.handleSubmit(onSubmitActivatedUserWithPassword)}
                className={cn("space-y-4")}
              >
                <InviteTextField
                  control={passwordOnlyForm.control}
                  name="password"
                  label={t("invite.workspace.passwordLabel")}
                  type="password"
                />
                <div className="flex flex-row items-center justify-start">
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => sendPasswordReset()}
                    disabled={sendingReset}
                    className="h-auto p-0 text-sm font-normal text-muted-foreground"
                  >
                    {t("invite.workspace.forgotPassword")}
                  </Button>
                </div>
                <div className="flex flex-row items-center justify-end pt-4">
                  <AcceptInviteButton isLoading={loading} type="submit" />
                </div>
              </form>
            </Form>
          </div>
        )}

        {canShowPasswordForm && !user.hasSetPassword && (
          <div className="flex flex-col gap-1 ">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmitNewUserWithPassword)} className={cn("space-y-4")}>
                <InviteTextField
                  control={form.control}
                  name="email"
                  label={t("invite.workspace.emailLabel")}
                  disabled={true}
                />
                <InviteTextField
                  control={form.control}
                  name="fullname"
                  label={t("invite.workspace.nameLabel")}
                  placeholder="Maxi Musterfrau"
                />
                <InviteTextField
                  control={form.control}
                  name="username"
                  label={t("invite.workspace.usernameLabel")}
                  placeholder="Maxi"
                />
                <InviteTextField
                  control={form.control}
                  name="password"
                  label={t("invite.workspace.passwordLabel")}
                  type="password"
                />
                <InviteTextField
                  control={form.control}
                  name="confirmation"
                  label={t("invite.workspace.confirmPasswordLabel")}
                  type="password"
                />
                <div className="flex flex-row items-center justify-end pt-4">
                  <AcceptInviteButton isLoading={loading} type="submit" />
                </div>
              </form>
            </Form>
          </div>
        )}

        {currentUser && currentUser.email !== user.email && (
          <div className="mt-6 w-full border-t border-solid pt-2">
            <p className="text-sm font-light text-foreground">
              {t("invite.workspace.alreadyLoggedInAs", { email: currentUser.email, workspace: workspace.name })}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
